import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { ArrowLeft, User, Building, AlertCircle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import {
  cancelGoogleSignUp,
  completeGoogleSignUp
} from "../../store/auth/authActions";
import { useNavigate } from "react-router-dom";

const GoogleRoleSelectionStep = ({ onBack, onComplete }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoading, googleSignInData } = useSelector((state) => state.auth);

  const [selectedRole, setSelectedRole] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleRoleSelect = useCallback((role) => {
    setSelectedRole(role);
    if (role === "seller") {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
      setCompanyName("");
      setPostalCode("");
    }
  }, []);

  const handleCancel = async () => {
    try {
      await dispatch(cancelGoogleSignUp()).unwrap();
      if (onBack) onBack();
    } catch (error) {
      console.error("Failed to cancel Google sign-up:", error);
      toast({
        title: "Error",
        description: "Failed to cancel sign-up. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleComplete = async () => {
    if (!selectedRole) {
      toast({
        title: "Selection Required",
        description: "Please select your account type.",
        variant: "destructive"
      });
      return;
    }

    // Validate seller fields
    if (selectedRole === "seller") {
      if (!companyName.trim()) {
        toast({
          title: "Company Name Required",
          description: "Please enter your company name.",
          variant: "destructive"
        });
        return;
      }
      if (!postalCode.trim()) {
        toast({
          title: "Postal Code Required",
          description: "Please enter your postal code.",
          variant: "destructive"
        });
        return;
      }
    }

    setIsCompleting(true);

    try {
      const callback = (role) => {
        if (role === "seller") {
          navigate("/list-inventory");
        } else {
          navigate("/profile");
        }
        if (onComplete) onComplete();
      };

      await dispatch(
        completeGoogleSignUp({
          role: selectedRole,
          companyName: selectedRole === "seller" ? companyName.trim() : null,
          postalCode: selectedRole === "seller" ? postalCode.trim() : null,
          callback
        })
      ).unwrap();
    } catch (error) {
      console.error("Failed to complete Google sign-up:", error);

      // Handle specific error cases
      let errorMessage = "Failed to complete sign-up. Please try again.";

      if (error.includes("No authenticated Firebase user")) {
        errorMessage = "Authentication expired. Please sign in again.";
        // Redirect back to auth
        if (onBack) onBack();
      } else if (error.includes("No user data found")) {
        errorMessage = "User data not found. Please sign in again.";
        if (onBack) onBack();
      }

      toast({
        title: "Sign-up Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsCompleting(false);
    }
  };

  const isFormValid =
    selectedRole &&
    (selectedRole === "buyer" ||
      (selectedRole === "seller" && companyName.trim() && postalCode.trim()));

  // Show error if no Google sign-in data
  if (!googleSignInData?.user) {
    return (
      <div className="space-y-6 text-center">
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl font-semibold flex items-center justify-center gap-2">
            <AlertCircle className="w-5 h-5 text-destructive" />
            Session Expired
          </DialogTitle>
          <DialogDescription className="text-sm">
            Your Google sign-in session has expired. Please try again.
          </DialogDescription>
        </DialogHeader>

        <Button onClick={handleCancel} variant="outline" className="w-full">
          Back to Sign In
        </Button>
      </div>
    );
  }

  if (!showAdditionalFields) {
    return (
      <div className="space-y-6">
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl font-semibold">
            How do you want to use your account?
          </DialogTitle>
          <DialogDescription className="text-sm">
            Choose your role to complete the Google sign-up.
          </DialogDescription>
        </DialogHeader>

        <div className="text-center space-y-3">
          <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
            <span>Signing in as:</span>
            <span className="font-medium">{googleSignInData?.user?.email}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 pt-4">
          {[
            {
              role: "buyer",
              label: "Continue as Buyer",
              icon: <User className="w-6 h-6" />
            },
            {
              role: "seller",
              label: "Continue as Seller",
              icon: <Building className="w-6 h-6" />
            }
          ].map(({ role, label, icon }) => (
            <Button
              key={role}
              variant="outline"
              className={`h-20 text-lg flex flex-col gap-2 border-2 transition-all duration-300
        ${
          selectedRole === role
            ? "border-primary bg-primary/10 shadow-[0_0_10px_rgba(255,214,90,0.5)] scale-105"
            : "border-muted hover:border-primary/40"
        }`}
              onClick={() => handleRoleSelect(role)}
              disabled={isLoading || isCompleting}
            >
              {icon}
              <span>{label}</span>
            </Button>
          ))}
        </div>

        {selectedRole === "buyer" && (
          <DialogFooter className="pt-4 flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isLoading || isCompleting}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              onClick={handleComplete}
              disabled={isLoading || isCompleting}
              className="w-full sm:w-auto"
            >
              {isCompleting ? "Completing sign-up..." : "Continue"}
            </Button>
          </DialogFooter>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DialogHeader className="text-center">
        <DialogTitle className="text-xl font-semibold">
          Additional Information Required
        </DialogTitle>
        <DialogDescription className="text-sm">
          As a seller, we need a few extra details.
        </DialogDescription>
      </DialogHeader>

      <div className="text-center space-y-3">
        <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
          <span>Registering as:</span>
          <span className="font-medium">{googleSignInData?.user?.email}</span>
        </div>
        <div className="flex items-center justify-center space-x-2 text-sm">
          <Building className="w-4 h-4" />
          <span className="font-medium">Seller</span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="google-company-name">Company Name *</Label>
          <Input
            id="google-company-name"
            type="text"
            placeholder="Example Auto Parts Inc."
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            disabled={isLoading || isCompleting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="google-postal-code">Postal Code *</Label>
          <Input
            id="google-postal-code"
            type="text"
            placeholder="A1B 2C3"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            required
            disabled={isLoading || isCompleting}
          />
        </div>
      </div>

      <DialogFooter className="pt-4 flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setShowAdditionalFields(false)}
          disabled={isLoading || isCompleting}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button
          onClick={handleComplete}
          disabled={!isFormValid || isLoading || isCompleting}
          className="w-full sm:w-auto"
        >
          {isCompleting ? "Completing sign-up..." : "Continue"}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default GoogleRoleSelectionStep;
